import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Bookmark, Plus, Trash2, ChevronDown, Users } from "lucide-react";
import MultiSelect from "@/components/MultiSelect";

export default function SavedViews({ page, filters, fields = [], onApply }) {
  const [views, setViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");
  const [draft, setDraft] = useState({});
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/saved-views?page=${page}`).then((r) => setViews(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [page]);
  useEffect(() => { load(); }, [load]);

  const openSave = () => {
    // Seed the editor with the current live filters so the user can tweak from there
    setDraft({ ...filters });
    setName("");
    setShared(false);
    setSaveOpen(true);
  };
  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const toggleMulti = (k, val) => setDraft((d) => {
    const cur = Array.isArray(d[k]) ? d[k] : [];
    return { ...d, [k]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] };
  });

  const save = async () => {
    if (!name.trim()) { toast.error("Name your view"); return; }
    setSaving(true);
    try {
      await api.post("/saved-views", { page, name: name.trim(), filters: draft, shared });
      toast.success("View saved");
      setSaveOpen(false);
      load();
    } catch { toast.error("Failed to save view"); }
    finally { setSaving(false); }
  };

  const remove = async (e, v) => {
    e.preventDefault(); e.stopPropagation();
    await api.delete(`/saved-views/${v.id}`).catch(() => {});
    setViews((prev) => prev.filter((x) => x.id !== v.id));
    toast.success("View removed");
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="gap-2" data-testid="saved-views-trigger">
            <Bookmark className="h-4 w-4" /> Views <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {views.length === 0 ? (
            <div className="px-2 py-3 text-xs text-muted-foreground">No saved views yet.</div>
          ) : (
            views.map((v) => (
              <DropdownMenuItem key={v.id} data-testid={`saved-view-${v.id}`}
                                onClick={() => { onApply(v.filters || {}); toast.success(`Applied “${v.name}”`); }}
                                className="flex items-center justify-between gap-2">
                <span className="truncate flex items-center gap-1.5">
                  {v.name}
                  {v.shared && (
                    <span data-testid={`shared-badge-${v.id}`} title={v.is_owner ? "Shared with your team" : `Shared by ${v.owner_name || "a teammate"}`}
                          className="inline-flex items-center gap-0.5 text-[10px] font-medium rounded-full px-1.5 py-0.5 bg-[#E7F0EC] text-[#0B3D3D]">
                      <Users className="h-2.5 w-2.5" /> Team
                    </span>
                  )}
                </span>
                {v.is_owner ? (
                  <button data-testid={`delete-view-${v.id}`} onClick={(e) => remove(e, v)}
                          className="text-muted-foreground hover:text-[#C24127] shrink-0">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <span className="text-[10px] text-muted-foreground shrink-0 truncate max-w-[70px]">{v.owner_name}</span>
                )}
              </DropdownMenuItem>
            ))
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="save-view-open" onClick={openSave} className="gap-2 text-[#0B3D3D] font-medium">
            <Plus className="h-4 w-4" /> Create new view
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent data-testid="save-view-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Create a view</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Name your view and choose the filters it should apply.</p>

          <div className="py-1">
            <Label>View name</Label>
            <Input data-testid="save-view-name" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. My overdue reships" className="mt-1.5" autoFocus />
          </div>

          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-[#F7FAF8] px-3 py-2.5">
            <div>
              <Label className="text-sm flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-[#0B3D3D]" /> Share with team</Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">Everyone on the team can apply this view.</p>
            </div>
            <Switch data-testid="save-view-shared" checked={shared} onCheckedChange={setShared} />
          </div>

          <div className="space-y-3 pt-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Filters</p>
            {fields.map((f) => {
              if (f.type === "text") {
                return (
                  <div key={f.key}>
                    <Label className="text-sm">{f.label}</Label>
                    <Input data-testid={`view-field-${f.key}`} value={draft[f.key] || ""}
                           onChange={(e) => setField(f.key, e.target.value)} className="mt-1" placeholder={f.placeholder || ""} />
                  </div>
                );
              }
              if (f.type === "select") {
                return (
                  <div key={f.key} className="flex items-center justify-between gap-3">
                    <Label className="text-sm">{f.label}</Label>
                    <Select value={draft[f.key] ?? f.options[0]?.value} onValueChange={(v) => setField(f.key, v)}>
                      <SelectTrigger className="w-48 h-9" data-testid={`view-field-${f.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                );
              }
              if (f.type === "boolean") {
                return (
                  <div key={f.key} className="flex items-center justify-between gap-3">
                    <Label className="text-sm">{f.label}</Label>
                    <Switch data-testid={`view-field-${f.key}`} checked={!!draft[f.key]} onCheckedChange={(v) => setField(f.key, v)} />
                  </div>
                );
              }
              if (f.type === "multiselect") {
                const cur = Array.isArray(draft[f.key]) ? draft[f.key] : [];
                return (
                  <div key={f.key} className="flex items-center justify-between gap-3">
                    <Label className="text-sm">{f.label}</Label>
                    <MultiSelect allLabel={`All ${f.label.toLowerCase()}`} testid={`view-field-${f.key}`} width="w-52"
                      options={f.options} value={cur} onChange={(v) => setField(f.key, v)} />
                  </div>
                );
              }
              if (f.type === "multi") {
                const cur = Array.isArray(draft[f.key]) ? draft[f.key] : [];
                return (
                  <div key={f.key}>
                    <Label className="text-sm">{f.label}</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid={`view-field-${f.key}`}>
                      {f.options.map((o) => {
                        const on = cur.includes(o.value);
                        return (
                          <button key={o.value} type="button" data-testid={`view-multi-${f.key}-${o.value}`}
                                  onClick={() => toggleMulti(f.key, o.value)}
                                  className={`text-[11px] font-medium rounded-full px-2.5 py-1 border transition-colors ${
                                    on ? "bg-[#FBE4DC] text-[#C24127] border-[#E6B7A9] line-through" : "bg-[#0B3D3D] text-white border-[#0B3D3D]"
                                  }`}>
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                    {f.hint && <p className="text-[11px] text-muted-foreground mt-1">{f.hint}</p>}
                  </div>
                );
              }
              return null;
            })}
          </div>

          <DialogFooter className="pt-2">
            <Button variant="ghost" onClick={() => setSaveOpen(false)} data-testid="save-view-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-view-confirm" className="bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">Save view</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
